import { useEffect, useRef } from 'react';
import { useMarketStore } from '../services/store';
import { useSettingsStore } from '../stores/settingsStore';
import { usePrecomputedStore } from '../stores/precomputeStore';
import { runInWorker } from '../components/GlobalHistoryFetcher';
import { timeToMin, extractTimeMinutes } from '../lib/snapshot-search';

const EMPTY_ARRAY = Object.freeze([]);

export function usePrecomputeEngine() {
    const isLive = useSettingsStore(state => state.isLive);
    const symbol = useSettingsStore(state => state.symbol);
    const rawExpiry = useSettingsStore(state => state.expiry);
    const baseTimePattern = useSettingsStore(state => state.baseTime);
    const range = useSettingsStore(state => state.range);
    const mode = useSettingsStore(state => state.mode);
    const inspectionSource = useSettingsStore(state => state.inspectionSource);

    const symbolExpiries = useMarketStore(state => state.expiryMap[symbol] || EMPTY_ARRAY);
    const expiry = rawExpiry || (symbolExpiries.length > 0 ? (symbolExpiries[0] as string) : '');
    
    const historyLength = useMarketStore( ( state ) => {
        if ( inspectionSource === 'HistoricalSnapshot' ) return state.historicalHistory?.[ symbol ]?.length ?? 0
        if ( inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD' ) return state.rangeHistory?.[ symbol ]?.length ?? 0
        return state.intradayHistory?.[ symbol ]?.length ?? 0
    } );
    
    const { setTimeline, setIsPrecomputing, timelines } = usePrecomputedStore();
    
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Only precompute in historical modes
        if (isLive || historyLength === 0) return;

        const cacheKey = `${symbol}_${expiry}_${range}_${mode}_${baseTimePattern}_${historyLength}`;
        
        // Already precomputed
        if (timelines[cacheKey]) return;

        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        
        debounceTimer.current = setTimeout(async () => {
            setIsPrecomputing(true);
            try {
                const storeState = useMarketStore.getState();
                let fullHistory: any[] = [];
                if ( inspectionSource === 'HistoricalSnapshot' ) {
                    fullHistory = storeState.historicalHistory?.[ symbol ] || [];
                } else if ( inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD' ) {
                    fullHistory = storeState.rangeHistory?.[ symbol ] || [];
                } else {
                    fullHistory = storeState.intradayHistory?.[ symbol ] || [];
                }

                if (fullHistory.length === 0) {
                    setIsPrecomputing(false);
                    return;
                }

                let baseSnapshot = null;
                if (baseTimePattern) {
                    const targetMinBase = timeToMin(baseTimePattern);
                    let bestDiffBase = Infinity;
                    for (const snap of fullHistory) {
                        const snapMin = extractTimeMinutes(snap);
                        if (isNaN(snapMin)) continue;
                        const diff = Math.abs(targetMinBase - snapMin);
                        if (diff === 0) { baseSnapshot = snap; break; }
                        if (diff < bestDiffBase) { bestDiffBase = diff; baseSnapshot = snap; }
                    }
                }
                
                // Using runInWorker from GlobalHistoryFetcher
                const frames = await runInWorker('COMPUTE_TIMELINE_AOT', {
                    history: fullHistory, // raw history via structured clone
                    baseSnapshot,
                    symbol,
                    expiry,
                    mode,
                    range,
                    baseTimePattern
                });
                
                setTimeline(cacheKey, frames);
            } catch (err) {
                console.error('[usePrecomputeEngine] Failed to precompute timeline:', err);
                setIsPrecomputing(false);
            }
        }, 500); // 500ms debounce to prevent thrashing when user rapidly changes filters

        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [isLive, symbol, expiry, range, mode, baseTimePattern, historyLength, timelines, inspectionSource]);
}
